import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRetrieveReview } from "../../hooks/review/useRetrieveReview";
import {
  QAlert,
  QBackLink,
  QBodyLayout,
  QBox,
  QButton,
  QButtonGroup,
  QDateMetadataItem,
  QDeleteConfirmationModal,
  QHeader,
  QHorizontalMetadata,
  QIconButton,
  QMenuButton,
  QMenuItem,
  QNotFound,
  QOpenPropertiesPanelButton,
  QOwnerMetadata,
  QPageLoader,
  QPerson,
  QPersonProps,
  QPropertiesPanel,
  QStack,
  QTab,
  QTabList,
  QTabPanel,
  QTabPanels,
  QTabs,
  QText,
  QTitle,
  QTogglePropertiesPanelButton,
  QTooltip,
  useCurrentUser,
} from "@qualio/ui-components";
import { ReviewStatus, ReviewStatusEnum } from "@design-controls/types";
import { useCurrentProduct } from "../../hooks/useCurrentProduct";
import { PermissionCheck } from "../../components/PermissionCheck/PermissionCheck";
import { Outlet, useNavigate } from "react-router";
import { useSendForApproval } from "../../hooks/review/useSendForApproval";
import { useRevertToDraft } from "../../hooks/review/useRevertToDraft";
import { useDisclosure } from "@chakra-ui/hooks";
import { useDeleteReview } from "../../hooks/review/useDeleteReview";
import { useRetrieveGeneratedDocuments } from "../../hooks/review/useRetrieveGeneratedDocuments";
import { ReviewStatusTag } from "./components/ReviewStatusTag";
import { ReviewTraceability } from "../ReviewTraceability/ReviewTraceability";
import { ReviewDetail } from "./components/ReviewDetails";
import { useReferenceDrawer } from "../../hooks/useReferenceDrawer";
import { StepBlocker } from "../../components/StepBlocker/StepBlocker";
import { UserPermissions } from "../../domain/permissions";

const statusTable: Partial<Record<ReviewStatus, QPersonProps["status"]>> = {
  [ReviewStatus.enum.IN_PROGRESS]: "default",
  [ReviewStatus.enum.NOT_APPROVED]: "declined",
  [ReviewStatus.enum.APPROVED]: "approved",
};

const TABS = (t: (key: string) => string) => [
  {
    id: "detail",
    title: t("reviews.overview.tabs.details"),
  },
  {
    id: "traceability",
    title: t("reviews.overview.tabs.traceability"),
  },
];

export const ReviewOverview: React.FC = () => {
  const { t } = useTranslation();
  const { isLoading, data } = useRetrieveReview();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [active, setActive] = useState(0);

  const {
    data: reportData,
    isComplete,
    isLoading: isLoadingGeneratedDocs,
    downloadExportPacket,
  } = useRetrieveGeneratedDocuments(data?.releaseId);

  const { formatDate, userId } = useCurrentUser();
  const { name } = useCurrentProduct();
  const navigate = useNavigate();

  const { mutate: mutateForApproval, isLoading: isSendForApprovalLoading } =
    useSendForApproval();

  const { mutate: mutateForDraft, isLoading: isRevertToDraftLoading } =
    useRevertToDraft();

  const { isLoading: isDeleting, mutate: deleteReview } = useDeleteReview();

  const onEdit = useCallback(
    () => navigate(`../change-control/${data?.id}/edit`),
    [navigate, data],
  );

  const { handleClick } = useReferenceDrawer(
    "design-controls-changeControl",
    data?.id,
    data?.title,
  );

  const isOwner = useMemo(
    () => userId === Number(data?.createdBy?.id),
    [userId, data],
  );

  const canApprove = useMemo(() => {
    if (data?.status !== ReviewStatus.enum.SENT_FOR_APPROVAL) {
      return false;
    }

    return !!data.approvers.find(
      (approver) =>
        Number(approver.id) === userId &&
        approver.status === ReviewStatus.enum.IN_PROGRESS,
    );
  }, [userId, data]);

  const sendForApproval = useCallback(
    () => mutateForApproval(),
    [mutateForApproval],
  );

  const onConfirmDelete = useCallback(() => {
    if (!data || !data.id) {
      return;
    }

    deleteReview(data.id);
  }, [data, deleteReview]);

  const revertToDraft = useCallback(() => mutateForDraft(), [mutateForDraft]);

  const hasOutdatedDesignElements = useMemo(() => {
    return (
      data?.qualityItems.some(
        (item) => item.latest && item.latest !== item.version,
      ) &&
      ![ReviewStatusEnum.APPROVED, ReviewStatusEnum.NOT_APPROVED].includes(
        data.status,
      )
    );
  }, [data]);

  if (isLoading) {
    return <QPageLoader />;
  }

  if (!data) {
    return <QNotFound />;
  }

  return (
    <>
      <QStack overflow="hidden">
        <QBodyLayout.Default>
          <QHeader showDivider={!data.releaseId}>
            <QBackLink href="../change-control" data-cy="back-btn">
              {t("products.backLink", { productName: name })}
            </QBackLink>
            <QTitle data-cy="review-title">
              <QText as="span" color="gray.500">
                {data.code}
              </QText>{" "}
              {data.title}
            </QTitle>
            {hasOutdatedDesignElements && (
              <QAlert
                description={
                  isOwner
                    ? t("reviews.overview.alerts.outdatedDesignElementsOwner")
                    : t(
                        "reviews.overview.alerts.outdatedDesignElementsNonOwner",
                      )
                }
                status="warning"
                data-cy="outdated-design-elements"
              />
            )}
            <QHorizontalMetadata>
              {data.status && <ReviewStatusTag status={data.status} />}
              <QDateMetadataItem
                label={t("reviews.overview.metadata.created")}
                value={new Date(data?.created ?? 0)}
              />
              <QDateMetadataItem
                label={t("reviews.overview.metadata.lastModified")}
                value={new Date(data?.updated ?? 0)}
              />
              <QOwnerMetadata value={data.createdBy?.fullName} />
            </QHorizontalMetadata>
            <QButtonGroup>
              <QTooltip
                label={t("reviews.overview.relatedRecords")}
                placement="top"
              >
                <QBox>
                  <QIconButton
                    variant="outline"
                    onClick={handleClick}
                    iconName="Related"
                    aria-label={t("reviews.overview.relatedRecords")}
                    data-cy="related-records-button"
                  />
                </QBox>
              </QTooltip>
              {isComplete && (
                <QButton
                  data-cy="export-review-packet"
                  onClick={downloadExportPacket}
                  variant="outline"
                >
                  {t("reviews.overview.actions.exportReviewPacket")}
                </QButton>
              )}
              <PermissionCheck
                permissions={[UserPermissions.EDIT_CHANGE_CONTROL]}
              >
                {isOwner && data.status === ReviewStatus.enum.IN_PROGRESS && (
                  <QButton
                    leftIcon="Edit3"
                    variant="outline"
                    data-cy="edit"
                    onClick={onEdit}
                  >
                    {t("reviews.overview.actions.edit")}
                  </QButton>
                )}
                {isOwner && data.status === ReviewStatus.enum.IN_PROGRESS && (
                  <QButton
                    data-cy="send-for-approval-button"
                    onClick={sendForApproval}
                    isLoading={isSendForApprovalLoading}
                  >
                    {t("reviews.overview.actions.sendForApproval")}
                  </QButton>
                )}
                {isOwner &&
                  data.status === ReviewStatus.enum.SENT_FOR_APPROVAL && (
                    <QButton
                      data-cy="send-for-draft-button"
                      variant="outline"
                      onClick={revertToDraft}
                      isLoading={isRevertToDraftLoading}
                    >
                      {t("reviews.overview.actions.revertToDraft")}
                    </QButton>
                  )}
                {isOwner && data.status === ReviewStatus.enum.IN_PROGRESS && (
                  <QMenuButton
                    data-cy="more"
                    iconName="MoreVertical"
                    itemSize="sm"
                    variant="icon"
                  >
                    <QMenuItem
                      isDestructive
                      onClick={onOpen}
                      data-cy="delete-review"
                    >
                      {t("reviews.overview.actions.delete")}
                    </QMenuItem>
                  </QMenuButton>
                )}
              </PermissionCheck>
              <QOpenPropertiesPanelButton />
            </QButtonGroup>
            {!data.approvers.length && (
              <QAlert
                title={t("reviews.overview.alerts.missingApprovers.title")}
                description={t(
                  "reviews.overview.alerts.missingApprovers.description",
                )}
                status="warning"
              />
            )}
          </QHeader>
          <QStack data-cy="review-overview">
            {!isComplete ? (
              <ReviewDetail
                canApprove={canApprove}
                data={data}
                reportData={reportData}
                isComplete={isComplete}
                isLoading={isLoadingGeneratedDocs}
                hasOutdatedDesignElements={hasOutdatedDesignElements}
              />
            ) : (
              <QTabs onChange={setActive}>
                <QTabList>
                  {TABS(t).map((tab) => (
                    <QTab key={tab.title} data-cy={tab.title}>
                      {tab.title}
                    </QTab>
                  ))}
                </QTabList>
                <QTabPanels>
                  <QTabPanel>
                    <StepBlocker isActive={active === 0}>
                      <ReviewDetail
                        canApprove={canApprove}
                        data={data}
                        reportData={reportData}
                        isComplete={isComplete}
                        isLoading={isLoadingGeneratedDocs}
                        hasOutdatedDesignElements={hasOutdatedDesignElements}
                      />
                    </StepBlocker>
                  </QTabPanel>
                  <QTabPanel>
                    <StepBlocker isActive={active === 1}>
                      <ReviewTraceability releaseId={data.releaseId} />
                    </StepBlocker>
                  </QTabPanel>
                </QTabPanels>
              </QTabs>
            )}
          </QStack>
          <QPropertiesPanel openOnMount>
            <QStack spacing="8px" w="300px">
              <QStack direction="row" justifyContent="space-between">
                <QTitle>{t("reviews.overview.properties.title")}</QTitle>
                <QTogglePropertiesPanelButton />
              </QStack>
              <QStack>
                <QText fontSize="sm" fontWeight={600}>
                  {t("reviews.overview.properties.approvers")}
                </QText>
                <QStack>
                  {data.approvers.map((approver) => (
                    <QStack>
                      <QPerson
                        key={approver.id}
                        status={
                          statusTable[approver.status as ReviewStatus] ??
                          "default"
                        }
                        actionTimestamp={
                          approver.modified
                            ? formatDate(new Date(approver.modified))
                            : undefined
                        }
                        fullName={approver.fullName ?? ""}
                      />
                      {approver.note && (
                        <QText as="i" fontSize="xs" mt={1} color="gray.700">
                          {approver.note}
                        </QText>
                      )}
                    </QStack>
                  ))}
                </QStack>
              </QStack>
            </QStack>
          </QPropertiesPanel>
        </QBodyLayout.Default>
      </QStack>
      <Outlet />
      {isOpen && (
        <QDeleteConfirmationModal
          onConfirm={onConfirmDelete}
          onClose={onClose}
          title={t("reviews.delete.modalTitle")}
          inProgress={isDeleting}
          message={
            <QText fontSize="sm">
              {t("reviews.delete.confirmMessage", { reviewTitle: data?.title })}
            </QText>
          }
        />
      )}
    </>
  );
};
